/**
 * Pre-auth pages — the two places a person lands with a token and no session.
 *
 * Both are reached from an email by someone who CANNOT sign in: a new customer
 * confirming a signup, or a closed-out one coming back to subscribe. They must
 * therefore render before AuthGate does any session work at all, which is why
 * AuthGate pins them by pathname first (same idiom as /reset-password).
 *
 * Deliberately self-contained: no router, no data-source, no shared chrome.
 * These have to work when everything behind them is unavailable to the viewer,
 * so they use plain fetch and inline Citadel styling rather than importing the
 * application shell they are, by definition, outside of.
 */

import { useEffect, useState } from 'react';

const NAVY = '#1a1f2e';
const CREAM = '#f6f4f0';
const CRIMSON = '#8b1a2e';
const BORDER = '#d0ccc4';
const MUTED = '#636670';

function tokenFromQuery(): string {
  try {
    return new URLSearchParams(window.location.search).get('token') ?? '';
  } catch {
    return '';
  }
}

// ── Shared shell ────────────────────────────────────────────────────────────

/**
 * Same chrome as the landing page: navy band, crimson rule, cream field, navy
 * footer. These pages previously rendered as a bare card floating on cream,
 * which read as a different product — and they are the FIRST thing a new
 * customer sees after clicking an email, so they carry the first impression of
 * whether this is a serious tool. Values are copied from SplashScreen's styles
 * deliberately rather than imported: this file must keep working when the UI
 * package behind it is unavailable to the viewer.
 */
function Shell({ kicker, title, children }: { kicker: string; title: string; children: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', background: CREAM, display: 'flex', flexDirection: 'column' }}>
      <div style={{ background: NAVY, color: CREAM, padding: '18px 56px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
          <span style={{ fontFamily: 'Georgia, serif', fontSize: 24, letterSpacing: '0.5px' }}>Pentest Solutions</span>
          <span style={{ fontFamily: "'Courier New', monospace", fontSize: 10, letterSpacing: '3px', textTransform: 'uppercase', color: 'rgba(246, 244, 240, 0.55)' }}>
            Penetration Testing Reports
          </span>
        </div>
      </div>
      <div style={{ height: 1.5, background: CRIMSON }} />

      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>
        <div style={{ width: '100%', maxWidth: 560, background: '#fff', border: `1px solid ${BORDER}`, padding: '38px 40px' }}>
          <div style={{ fontFamily: "'Courier New', monospace", fontSize: 9.5, letterSpacing: '2.4px', textTransform: 'uppercase', color: CRIMSON, paddingBottom: 10 }}>
            {kicker}
          </div>
          <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 26, fontWeight: 'normal', color: NAVY, margin: '0 0 16px', lineHeight: 1.25 }}>
            {title}
          </h1>
          <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 14, color: NAVY, lineHeight: 1.75 }}>
            {children}
          </div>
        </div>
      </div>

      <div style={{ background: NAVY, color: 'rgba(246, 244, 240, 0.55)', padding: '18px 56px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11 }}>
        <div style={{ fontFamily: "'Courier New', monospace", letterSpacing: '2.5px', textTransform: 'uppercase' }}>
          Pentest Solutions, Penetration Testing Reports
        </div>
        <div style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>UK-Built, for Pentest Firms</div>
      </div>
    </div>
  );
}

function PrimaryLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} style={{ display: 'inline-block', marginTop: 22, padding: '13px 26px', background: NAVY, color: '#fff', textDecoration: 'none', fontFamily: 'Georgia, serif', fontSize: 14, letterSpacing: '.3px' }}>
      {children}
    </a>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <div style={{ marginTop: 20, paddingTop: 14, borderTop: `1px solid ${BORDER}`, fontSize: 12, color: MUTED, lineHeight: 1.65 }}>{children}</div>;
}

// ── /signup/verify ──────────────────────────────────────────────────────────

interface VerifyState {
  phase: 'working' | 'done';
  ok?: boolean;
  kicker?: string;
  title?: string;
  body?: string;
  signInUrl?: string;
}

export function SignupVerifyPage() {
  const [s, setS] = useState<VerifyState>({ phase: 'working' });

  useEffect(() => {
    const token = tokenFromQuery();
    if (!token) {
      setS({ phase: 'done', ok: false, kicker: 'Link problem', title: 'That link is incomplete', body: 'It seems to have been cut short — email clients sometimes wrap long links. Try copying the whole address from the email into your browser.' });
      return;
    }
    void (async () => {
      try {
        const res = await fetch('/api/v1/signup/verify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        const j = await res.json().catch(() => ({}));
        if (res.ok && j.already_provisioned) {
          setS({ phase: 'done', ok: true, kicker: 'Already set up', title: 'Your workspace is ready', body: `${j.firm_name ?? 'Your workspace'} has already been created — this link had been opened before. Sign in to pick up where you left off.`, signInUrl: j.sign_in_url ?? '/auth/login' });
          return;
        }
        if (res.ok) {
          setS({ phase: 'done', ok: true, kicker: 'Workspace created', title: `${j.firm_name ?? 'Your workspace'} is ready`, body: 'Your free trial has started. Sign in to get going — we have sent an invitation to your address if this is your first time.', signInUrl: j.sign_in_url ?? '/auth/login' });
          return;
        }
        if (j.code === 'LINK_EXPIRED') {
          setS({ phase: 'done', ok: false, kicker: 'Link expired', title: 'This link has expired', body: 'Signup links are valid for a limited time. Sign up again and we will send you a fresh one.' });
          return;
        }
        if (j.code === 'TRIAL_ALREADY_USED') {
          setS({ phase: 'done', ok: false, kicker: 'Trial already used', title: 'This address has already had a trial', body: 'The free trial is once per address, and that workspace has since been closed. Subscribe to start again, or reply to our email and we will help.' });
          return;
        }
        setS({ phase: 'done', ok: false, kicker: 'Link problem', title: 'This link is not valid', body: j.message ?? 'Check you used the most recent email, or sign up again.' });
      } catch {
        setS({ phase: 'done', ok: false, kicker: 'Connection problem', title: 'We could not reach the server', body: 'Please check your connection and try the link again.' });
      }
    })();
  }, []);

  if (s.phase === 'working') {
    return <Shell kicker="One moment" title="Setting up your workspace…"><div style={{ color: MUTED }}>This usually takes a second or two.</div></Shell>;
  }

  return (
    <Shell kicker={s.kicker ?? ''} title={s.title ?? ''}>
      <div>{s.body}</div>
      {s.signInUrl ? <PrimaryLink href={s.signInUrl}>Sign in</PrimaryLink> : null}
      {!s.ok ? <Muted>If you think this is wrong, reply to the email you received — a person reads it.</Muted> : null}
    </Shell>
  );
}

// ── /billing/resume ─────────────────────────────────────────────────────────

export function BillingResumePage() {
  const [firmName, setFirmName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cycle, setCycle] = useState<'annual' | 'monthly'>('annual');
  const token = tokenFromQuery();

  useEffect(() => {
    if (!token) {
      setError('That link is incomplete. Try copying the whole address from the email.');
      return;
    }
    void (async () => {
      try {
        const res = await fetch(`/api/v1/billing/resume/${encodeURIComponent(token)}`);
        const j = await res.json().catch(() => ({}));
        if (!res.ok) {
          setError(j.message ?? 'This link is no longer valid.');
          return;
        }
        setFirmName(j.firm_name ?? null);
      } catch {
        setError('We could not reach the server. Please try again shortly.');
      }
    })();
  }, [token]);

  async function startCheckout() {
    setBusy(true);
    try {
      const res = await fetch('/api/v1/billing/resume', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, cycle }),
      });
      const j = await res.json().catch(() => ({}));
      if (res.ok && j.checkout_url) {
        window.location.href = j.checkout_url;
        return;
      }
      setError(j.message ?? 'We could not start the checkout. Please try again shortly.');
    } catch {
      setError('We could not reach the server. Please try again shortly.');
    } finally {
      setBusy(false);
    }
  }

  if (error) {
    return (
      <Shell kicker="Link problem" title="This link is no longer valid">
        <div>{error}</div>
        <Muted>Reply to the email you received and we will send you a new one.</Muted>
      </Shell>
    );
  }

  if (!firmName) {
    return <Shell kicker="One moment" title="Checking your link…"><div style={{ color: MUTED }}>Just a second.</div></Shell>;
  }

  return (
    <Shell kicker="Reactivate" title={`Restore ${firmName}`}>
      <div>
        Your workspace is closed because the subscription lapsed. Subscribe and it reopens with
        everything exactly where you left it — your clients, engagements and reports are all still there.
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
        {(['annual', 'monthly'] as const).map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setCycle(c)}
            style={{
              flex: 1, padding: '11px 14px', cursor: 'pointer',
              fontFamily: 'Georgia, serif', fontSize: 13,
              background: cycle === c ? NAVY : '#fff',
              color: cycle === c ? '#fff' : NAVY,
              border: `1px solid ${cycle === c ? NAVY : BORDER}`,
            }}
          >
            {c === 'annual' ? 'Annual' : 'Monthly'}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={() => void startCheckout()}
        disabled={busy}
        style={{
          display: 'block', width: '100%', marginTop: 14, padding: '13px 26px',
          background: busy ? MUTED : CRIMSON, color: '#fff', border: 'none',
          cursor: busy ? 'default' : 'pointer', fontFamily: 'Georgia, serif', fontSize: 14, letterSpacing: '.3px',
        }}
      >
        {busy ? 'Opening checkout…' : 'Continue to payment'}
      </button>
      <Muted>
        Payment is handled by Stripe — we never see your card details. Your workspace reopens as soon
        as the payment completes.
      </Muted>
    </Shell>
  );
}

// ── /billing/restore ────────────────────────────────────────────────────────

/**
 * "My workspace is closed and I want it back."
 *
 * Asks for an email rather than showing a checkout, because a checkout is bound
 * to a specific firm and an anonymous visitor is nobody yet. Their inbox is the
 * only evidence of identity once the account is deactivated, so the link is
 * emailed. Answers identically whatever the address turns out to be — the
 * server decides, and it tells the address, not the browser.
 */
export function BillingRestorePage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/billing/restore-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const j = await res.json().catch(() => ({}));
      if (res.ok) { setSent(true); return; }
      setError(j.message ?? 'Could not process that request.');
    } catch {
      setError('We could not reach the server. Please try again shortly.');
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <Shell kicker="Check your email" title="On its way">
        <div>
          If that address has a workspace with us, we have emailed a link to reopen it.
          It works without signing in.
        </div>
        <Muted>Nothing in the inbox after a few minutes? Check spam, then reply to any email from us.</Muted>
      </Shell>
    );
  }

  return (
    <Shell kicker="Restore access" title="Reopen a closed workspace">
      <div>
        Subscribe to reopen your workspace — everything is exactly where you left it. Tell us the
        email address you used and we will send a payment link. You do not need to sign in.
      </div>
      <form onSubmit={(e) => void submit(e)}>
        <input
          type="email"
          required
          value={email}
          onChange={(ev) => setEmail(ev.target.value)}
          placeholder="you@yourfirm.com"
          style={{ width: '100%', boxSizing: 'border-box', marginTop: 20, padding: '12px 14px', fontSize: 14, fontFamily: 'Arial, Helvetica, sans-serif', border: `1px solid ${BORDER}`, color: NAVY }}
        />
        <button
          type="submit"
          disabled={busy}
          style={{ display: 'block', width: '100%', marginTop: 12, padding: '13px 26px', background: busy ? MUTED : CRIMSON, color: '#fff', border: 'none', cursor: busy ? 'default' : 'pointer', fontFamily: 'Georgia, serif', fontSize: 14, letterSpacing: '.3px' }}
        >
          {busy ? 'Sending…' : 'Email me a payment link'}
        </button>
      </form>
      {error ? <div style={{ marginTop: 14, color: CRIMSON, fontSize: 13 }}>{error}</div> : null}
      <Muted>Still have access? <a href="/auth/login" style={{ color: NAVY }}>Sign in instead</a>.</Muted>
    </Shell>
  );
}

// ── /signup ─────────────────────────────────────────────────────────────────

/**
 * Start a free trial.
 *
 * The landing page's primary call to action lands here. Deliberately asks for
 * the minimum that provisioning actually needs — a firm name and an address —
 * because every extra field on a signup form costs conversions, and everything
 * else (profile, template, members) is better collected inside the product once
 * they have a reason to care.
 *
 * The response is the same whatever the address turns out to be: new, already
 * an account, or a spent trial. That is the server's no-enumeration contract
 * and this page must not undermine it by rendering different outcomes.
 */
export function SignupPage() {
  const [firmName, setFirmName] = useState('');
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<{ enabled: boolean; trial_days?: number; trial_seat_cap?: number } | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const r = await fetch('/api/v1/signup/status');
        setStatus(r.ok ? await r.json() : { enabled: false });
      } catch {
        setStatus({ enabled: false });
      }
    })();
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firm_name: firmName, email }),
      });
      const j = await res.json().catch(() => ({}));
      if (res.ok) { setSent(true); return; }
      setError(j.message ?? 'We could not process that. Please try again.');
    } catch {
      setError('We could not reach the server. Please try again shortly.');
    } finally {
      setBusy(false);
    }
  }

  if (status && !status.enabled) {
    return (
      <Shell kicker="Get in touch" title="Trials are arranged directly">
        <div>Self-serve signup isn&rsquo;t enabled here. Contact us and we&rsquo;ll set your workspace up.</div>
        <Muted>Already have an account? <a href="/auth/login" style={{ color: NAVY }}>Sign in</a>.</Muted>
      </Shell>
    );
  }

  if (sent) {
    return (
      <Shell kicker="Check your email" title="One more step">
        <div>
          If that address can start a trial, we&rsquo;ve sent a link to confirm it. Opening the link
          creates your workspace &mdash; nothing exists until you do.
        </div>
        <Muted>Nothing after a few minutes? Check spam, and confirm the address was right.</Muted>
      </Shell>
    );
  }

  const days = status?.trial_days ?? 7;
  // Seat allowance from the server, so the copy can never promise more than the
  // seat gate allows. The cap is the whole team INCLUDING the founder who is
  // signing up, so the number they can invite is cap - 1.
  const seats = status?.trial_seat_cap ?? 5;
  const invitees = Math.max(0, seats - 1);

  return (
    <Shell kicker="Free trial" title={`Start your ${days}-day trial`}>
      <div>
        Full access for {days} days. No card required. We&rsquo;ll email you a link to confirm your
        address, and your workspace is created when you open it.
      </div>
      {invitees > 0 && (
        <div style={{ marginTop: 16, padding: '12px 14px', background: '#fbfaf8', border: `1px solid ${BORDER}`, borderLeft: `3px solid ${CRIMSON}`, fontSize: 13, color: NAVY, lineHeight: 1.65 }}>
          <span style={{ fontFamily: 'Georgia, serif' }}>Bring Your Team;</span>{' '}
          Your trial includes {seats} {seats === 1 ? 'seat' : 'seats'}, so once you are in you can
          invite up to {invitees} {invitees === 1 ? 'colleague' : 'colleagues'} to explore alongside
          you.
        </div>
      )}
      <div style={{ marginTop: 12, padding: '12px 14px', background: '#fbfaf8', border: `1px solid ${BORDER}`, borderLeft: `3px solid ${CRIMSON}`, fontSize: 13, color: NAVY, lineHeight: 1.65 }}>
        <span style={{ fontFamily: 'Georgia, serif' }}>Use Test Data Only;</span>{' '}
        This is an evaluation trial. Please do not upload real customer or sensitive data; anything
        you add is at your own risk.
      </div>
      <form onSubmit={(e) => void submit(e)}>
        <label style={{ display: 'block', marginTop: 20, fontSize: 12, color: MUTED }}>Workspace Name</label>
        {/* Names the workspace only — report branding is configured separately
            in the app. Deliberately does NOT say so: spelling out what a field
            is not invites the reader to wonder why it needed saying. Two lines
            that answer "what do I type here" are enough. The examples matter
            though — the original assumed everyone works at a consultancy, which
            excludes the contractors and freelancers this is built for. */}
        <div style={{ fontSize: 12, color: MUTED, lineHeight: 1.6, marginTop: 4 }}>
          A reference for your workspace, so you and we can identify it. Your company, your trading
          name, or simply your own name.
        </div>
        <input
          required
          value={firmName}
          onChange={(ev) => setFirmName(ev.target.value)}
          placeholder="Your company, trading name, or your own name"
          style={{ width: '100%', boxSizing: 'border-box', marginTop: 6, padding: '12px 14px', fontSize: 14, fontFamily: 'Arial, Helvetica, sans-serif', border: `1px solid ${BORDER}`, color: NAVY }}
        />
        <label style={{ display: 'block', marginTop: 14, fontSize: 12, color: MUTED }}>Email Address</label>
        <div style={{ fontSize: 12, color: MUTED, lineHeight: 1.6, marginTop: 4 }}>
          Work or personal, whichever you actually check. This is where your link goes, and it
          becomes your sign-in.
        </div>
        <input
          type="email"
          required
          value={email}
          onChange={(ev) => setEmail(ev.target.value)}
          placeholder="you@example.com"
          style={{ width: '100%', boxSizing: 'border-box', marginTop: 6, padding: '12px 14px', fontSize: 14, fontFamily: 'Arial, Helvetica, sans-serif', border: `1px solid ${BORDER}`, color: NAVY }}
        />
        <button
          type="submit"
          disabled={busy}
          style={{ display: 'block', width: '100%', marginTop: 18, padding: '13px 26px', background: busy ? MUTED : CRIMSON, color: '#fff', border: 'none', cursor: busy ? 'default' : 'pointer', fontFamily: 'Georgia, serif', fontSize: 14, letterSpacing: '.3px' }}
        >
          {busy ? 'Sending…' : `Start My ${days}-Day Trial`}
        </button>
      </form>
      {error ? <div style={{ marginTop: 14, color: CRIMSON, fontSize: 13 }}>{error}</div> : null}
      <Muted>
        After the trial the workspace closes unless you subscribe, and we&rsquo;ll email you before
        that happens.
      </Muted>
      <div style={{ marginTop: 16, textAlign: 'center' }}>
        <span style={{ fontSize: 13, color: MUTED }}>Already have an account? </span>
        <a href="/auth/login" style={{ fontFamily: 'Georgia, serif', fontSize: 14, color: CRIMSON, textDecoration: 'none', borderBottom: `1px solid ${CRIMSON}`, paddingBottom: 1 }}>
          Sign in
        </a>
      </div>
    </Shell>
  );
}
